// handwritingOcr — turn a picked file into the payload POST /api/notes/ocr-image
// takes, for BOTH handwriting-capture surfaces (the note composer in
// components/shared/Notes.jsx and pages/GlobalNotes.jsx). One copy, because the
// two used to carry their own prepareImageForOcr and a PDF rule added to one
// would have been missing from the other.
//
// An image is downscaled and re-encoded as JPEG through a canvas — it shrinks a
// large HEIC photo under the request and Document Intelligence limits and
// normalizes the format to one every reader handles. A PDF cannot go through
// the canvas (an <img> will not decode one) and does not need to: the endpoint
// reads a PDF natively, so it is sent whole, base64-encoded, under its own
// content type — bounded here as well as on the server, so a 60 MB scan is
// refused before it is read into memory. Pure browser code, no app imports.

/** The largest PDF the OCR endpoint reads (it refuses past ~20 MB decoded). */
export const OCR_PDF_MAX_BYTES = 20 * 1024 * 1024;

export const isPdfFile = (file) =>
  (file?.type || '').toLowerCase() === 'application/pdf' || /\.pdf$/i.test(file?.name || '');

export const prepareImageForOcr = (file, maxDim = 2200, quality = 0.85) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        const base64 = canvas.toDataURL('image/jpeg', quality).split(',')[1];
        if (!base64) { reject(new Error('Could not process image')); return; }
        // Approximate decoded byte size of the base64 payload.
        const sizeBytes = Math.round(base64.length * 0.75);
        resolve({ base64, contentType: 'image/jpeg', sizeBytes });
      } catch (e) { reject(e); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read image')); };
    img.src = url;
  });

export const preparePdfForOcr = (file) =>
  new Promise((resolve, reject) => {
    if (file.size > OCR_PDF_MAX_BYTES) {
      reject(new Error('That PDF is too large to read here — keep it under 20 MB, or scan fewer pages at once.'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = String(reader.result || '').split(',')[1];
      if (!base64) { reject(new Error('Could not read PDF')); return; }
      resolve({ base64, contentType: 'application/pdf', sizeBytes: file.size });
    };
    reader.onerror = () => reject(new Error('Could not read PDF'));
    reader.readAsDataURL(file);
  });

/** One entry point for both: a PDF is read whole, an image is downscaled. */
export const prepareFileForOcr = (file) => (isPdfFile(file) ? preparePdfForOcr(file) : prepareImageForOcr(file));

/** The word a toast uses for the file, so "image" is never said of a PDF. */
export const ocrFileNoun = (file) => (isPdfFile(file) ? 'PDF' : 'image');

// ── Running the read ────────────────────────────────────────────────────────
// THE RESPONSE TO THE POST IS NOT THE OUTCOME. Both readers run inside the
// request, and a multi-page PDF outlives the ~45 s Static Web Apps edge, which
// answers with a BODYLESS 500 while the function carries on. So the client
// mints the job id, fires the POST, and — when that request fails — polls
// GET /api/notes-ocr-jobs/{jobId} for the row the server wrote regardless.
// The request's own error is surfaced ONLY when the row shows the job was
// never started (a 404, or a pre-200 server with no job table); otherwise the
// row is the truth (the SageImport.jsx rule).
export const OCR_POLL_MS = 3000;
export const OCR_POLL_MAX_MS = 6 * 60 * 1000;

const mintJobId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.floor(Math.random() * 16);
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const messageOf = async (res, fallback) => {
  const err = await res.json().catch(() => ({}));
  return err.detail || err.error || fallback;
};

/**
 * Run one handwriting read. Resolves `{ text, markdown, jobId }`; rejects
 * with the reason in words. `fetchImpl`/`sleepImpl`/`now` are injectable
 * for tests only.
 *
 * `fileId` reads a file already in a Notes-app note instead: the server reads
 * it from storage (POST /api/notebook-files/{id}/ocr), so nothing is sent up,
 * and the same job row answers the poll.
 */
export async function runHandwritingOcr({ base64, contentType, fileId, getAccessToken, onStatus,
  fetchImpl = (...a) => fetch(...a), sleepImpl = sleep, now = () => Date.now() }) {
  const jobId = mintJobId();
  const token = await getAccessToken();
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

  let postError = null;
  try {
    const res = fileId
      ? await fetchImpl(`/api/notebook-files/${encodeURIComponent(fileId)}/ocr`, {
        method: 'POST', headers, body: JSON.stringify({ jobId }),
      })
      : await fetchImpl('/api/notes/ocr-image', {
        method: 'POST', headers,
        body: JSON.stringify({ imageBase64: base64, contentType, jobId }),
      });
    if (res.ok) {
      const data = await res.json();
      return { text: data.text || '', markdown: data.markdown || null, jobId: data.jobId || jobId };
    }
    const message = await messageOf(res, `Scan failed (${res.status})`);
    // A 4xx is the server's own refusal (a type it does not take, a PDF over
    // the cap) — final, and made before any row was written.
    if (res.status >= 400 && res.status < 500) { const e = new Error(message); e.final = true; throw e; }
    postError = new Error(message);   // a 5xx: the edge's cut, or a fault the row will name
  } catch (e) {
    if (e && e.final) throw e;
    postError = postError || e;       // a network failure mid-read is the same case as the cut
  }

  // The request failed or was cut off; the row says what actually happened.
  onStatus?.('Still reading… this can take a minute on a long PDF.');
  const started = now();
  while (now() - started < OCR_POLL_MAX_MS) {
    await sleepImpl(OCR_POLL_MS);
    const res = await fetchImpl(`/api/notes-ocr-jobs/${jobId}`, { headers });
    if (res.status === 404 || res.status === 503) throw postError;   // never started, or no job table
    if (!res.ok) continue;                                            // a transient read failure is not an answer
    const job = await res.json();
    if (job.status === 'ready') return { text: job.text || '', markdown: job.markdown || null, jobId };
    if (job.status === 'error') throw new Error(job.message || 'The read failed.');
  }
  throw new Error('The read is taking too long. Try again with fewer pages.');
}
